import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LockKey, DeviceMobile, EnvelopeSimple } from "@phosphor-icons/react";
import { useApi, fmtDate, errText } from "@/lib/api";
import { useMe } from "@/components/app/TrialBanner";
import { deviceKey } from "@/components/account/MfaChallenge";
import { MfaSetupDialog } from "@/components/account/MfaSetupDialog";
import { MfaCodeDialog } from "@/components/account/MfaCodeDialog";

const METHOD = { totp: "Authenticator app", email: "Email code" };

const Choice = ({ icon: Icon, title, desc, testid, onClick }) => (
  <button data-testid={testid} onClick={onClick} className="flex-1 min-w-[220px] text-left rounded-xl border border-[#2A2A2A] p-4 hover:border-[#00FF9D]/60 hover:bg-[#00FF9D]/5 transition-colors">
    <Icon size={20} className="text-[#00FF9D]" />
    <p className="mt-2 text-sm font-medium">{title}</p>
    <p className="mt-1 text-xs text-[#A3A3A3]">{desc}</p>
  </button>
);

export const SecuritySection = ({ required = false }) => {
  const api = useApi();
  const { data } = useQuery({ queryKey: ["mfa"], queryFn: () => api.get("/mfa/status") });
  const [setup, setSetup] = useState(null);
  const [action, setAction] = useState(null);
  const qc = useQueryClient();
  const { data: me } = useMe();
  const forget = async () => {
    try {
      const r = await api.post("/mfa/devices/forget");
      if (me) localStorage.removeItem(deviceKey(me.user_id));
      qc.invalidateQueries({ queryKey: ["mfa"] });
      toast.success(`Forgot ${r.forgotten} trusted ${r.forgotten === 1 ? "device" : "devices"}. They'll need a code next time.`);
    } catch (e) { toast.error(errText(e, "Couldn't forget devices")); }
  };
  if (!data) return null;
  return (
    <section data-testid="security-section" className="rounded-xl border border-[#2A2A2A] bg-[#121212] p-6">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-xl flex items-center gap-2"><LockKey className="text-[#00FF9D]" /> Two-step sign-in</h2>
        <span data-testid="mfa-status" className={`rounded-full border px-2.5 py-1 font-mono text-[11px] ${data.enabled ? "border-[#00FF9D]/50 text-[#00FF9D]" : "border-[#2A2A2A] text-[#6B6B6B]"}`}>
          {data.enabled ? "ON" : "OFF"}</span>
      </div>
      {data.enabled ? (
        <>
          <p data-testid="mfa-summary" className="mt-2 text-sm text-[#A3A3A3]">
            Using <span className="text-white">{METHOD[data.method]}</span> since {fmtDate(data.enabled_at)}. {data.backup_codes_left} backup codes left.
          </p>
          {data.backup_codes_left <= 3 && <p data-testid="mfa-low-backup" className="mt-1 text-xs text-[#FFD166]">You're running low on backup codes. Make new ones.</p>}
          <p data-testid="mfa-trusted-count" className="mt-1 text-xs text-[#A3A3A3]">{data.trusted_devices} trusted {data.trusted_devices === 1 ? "device skips" : "devices skip"} the code for up to 30 days.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button data-testid="mfa-new-backup-btn" onClick={() => setAction("backup")} className="rounded-full border border-[#2A2A2A] px-4 py-2 text-xs hover:border-white transition-colors">Get new backup codes</button>
            {data.trusted_devices > 0 && <button data-testid="mfa-forget-devices-btn" onClick={forget} className="rounded-full border border-[#2A2A2A] px-4 py-2 text-xs hover:border-white transition-colors">Forget all trusted devices</button>}
            <button data-testid="mfa-disable-btn" onClick={() => setAction("disable")} className="rounded-full border border-[#FF5C7A]/50 px-4 py-2 text-xs text-[#FF8FA3] hover:bg-[#FF5C7A]/10 transition-colors">Turn off</button>
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-[#A3A3A3] max-w-2xl">Add a second step after your password or Google sign-in, so a stolen password alone can't open your finances. {required ? "Your team owner requires it, so pick a method to continue." : "It's optional and you can turn it off any time."}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <Choice testid="mfa-choose-totp" icon={DeviceMobile} title="Authenticator app" desc="Codes from Google Authenticator, 1Password, Authy. Works offline." onClick={() => setSetup("totp")} />
            <Choice testid="mfa-choose-email" icon={EnvelopeSimple} title="Email code" desc="We email you a 6-digit code each time you sign in on a new session." onClick={() => setSetup("email")} />
          </div>
        </>
      )}
      {setup && <MfaSetupDialog method={setup} onClose={() => { setSetup(null); qc.invalidateQueries({ queryKey: ["me"] }); }} />}
      {action && <MfaCodeDialog action={action} method={data.method} onClose={() => setAction(null)} />}
    </section>
  );
};
